"use client";

import { useState } from "react";
import { Sparkles } from "lucide-react";
import { LegalLinks } from "@/components/legal/legal-links";
import { ModalTemplate } from "@/components/ui/templates/modal-template";
import type { ReleaseNotesPayload } from "@/lib/api/api-client";
import { TabButton } from "./tab-button";
import { NewsPanel } from "./news-panel";
import { FeedbackPanel } from "./feedback-panel";

/** Onglet de la popin. Exporté : les appelants d'`openModal` la visent. */
export type WhatsNewTab = "news" | "feedback";

interface WhatsNewModalProps {
  open: boolean;
  onClose: () => void;
  data: ReleaseNotesPayload | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  defaultEmail: string | null;
  pathname: string | null;
  appVersion: string | null;
  /** Onglet sur lequel s'ouvre la popin. La pastille de bêta ouvre
   *  « Signaler un problème » directement, l'ampoule reste sur « Nouveautés ». */
  initialTab: WhatsNewTab;
}

/**
 * "Quoi de neuf" popin : two tabs (Nouveautés / Donner mon avis) on top of the
 * shared `ModalTemplate` (focus-trap, Escape and ARIA dialog handled there).
 */
export function WhatsNewModal({
  open,
  onClose,
  data,
  loading,
  error,
  onRetry,
  defaultEmail,
  pathname,
  appVersion,
  initialTab,
}: WhatsNewModalProps) {
  const [tab, setTab] = useState<WhatsNewTab>(initialTab);
  const [wasOpen, setWasOpen] = useState(open);

  // Each opening starts on the tab its trigger asked for (Nouveautés unless
  // stated otherwise); the feedback form below is remounted via `key={open}` so
  // a previous draft never lingers. We reset during render on the open→
  // transition (React's recommended alternative to an effect).
  if (open !== wasOpen) {
    setWasOpen(open);

    if (open) {
      setTab(initialTab);
    }
  }

  const handleTabKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      setTab((t) => (t === "news" ? "feedback" : "news"));
    }
  };

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Quoi de neuf"
      titleIcon={<Sparkles size={16} style={{ color: "var(--color-accent-bright)" }} />}
      size="lg"
      header={
        <div
          role="tablist"
          aria-label="Sections"
          onKeyDown={handleTabKey}
          className="flex gap-1"
        >
          <TabButton
            id="whatsnew-tab-news"
            panelId="whatsnew-panel-news"
            active={tab === "news"}
            onSelect={() => setTab("news")}
          >
            Nouveautés
          </TabButton>
          <TabButton
            id="whatsnew-tab-feedback"
            panelId="whatsnew-panel-feedback"
            active={tab === "feedback"}
            onSelect={() => setTab("feedback")}
          >
            Donner mon avis
          </TabButton>
        </div>
      }
      /*
        Le seul chemin vers les pages légales qui ne dépende de rien.

        La coque de l'application est plein écran et en `overflow: hidden` : il
        n'y a aucun pied de page à remplir, et les quatre points d'entrée qui
        existaient dépendaient tous de quelque chose — être dans le Studio pour
        les réglages, être en train de créer un compte, d'importer un fichier
        ou de consulter le répertoire pour les trois autres. Or la coque iOS
        veut la confidentialité atteignable sans compte et depuis n'importe
        quel écran.

        Le déclencheur de cette popin (l'ampoule, zone 3 du header) est la
        seule utilité transverse de la barre : présente sur toutes les routes,
        à tous les paliers, connecté ou non. C'est aussi déjà la surface qui
        parle du produit lui-même, notes de version et feedback, donc les
        documents qui l'encadrent y sont chez eux plutôt qu'en visite.
      */
      footer={
        <LegalLinks
          onNavigate={onClose}
          className="text-[11px] leading-[16px] text-text-muted"
        />
      }
    >
      {tab === "news" ? (
        <div
          role="tabpanel"
          id="whatsnew-panel-news"
          aria-labelledby="whatsnew-tab-news"
          className="flex-1 min-h-0 flex flex-col"
        >
          <NewsPanel data={data} loading={loading} error={error} onRetry={onRetry} />
        </div>
      ) : (
        <div
          role="tabpanel"
          id="whatsnew-panel-feedback"
          aria-labelledby="whatsnew-tab-feedback"
          className="flex-1 min-h-0 flex flex-col"
        >
          <FeedbackPanel
            key={String(open)}
            defaultEmail={defaultEmail}
            pathname={pathname}
            appVersion={appVersion}
          />
        </div>
      )}
    </ModalTemplate>
  );
}
